import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Music, X, Plus, Trash2, Play, Share2, Youtube, Loader2, Search, Users, Building2 } from "lucide-react";

const SPOTIFY_TALL = /\/embed\/(playlist|album|artist|show)\//;

// Biblioteca de medios reutilizable: se usa dentro del widget flotante y del módulo "Música".
export function MediaLibrary({ variant = "widget" }) {
  const isPage = variant === "page";
  const [tab, setTab] = useState("spotify");
  const [scope, setScope] = useState("mine"); // mine | school
  const [personal, setPersonal] = useState([]);
  const [shared, setShared] = useState([]);
  const [url, setUrl] = useState("");
  const [adding, setAdding] = useState(false);
  const [now, setNow] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const inputRef = useRef(null);
  const [shareOn, setShareOn] = useState(false);
  const [targets, setTargets] = useState({ people: [], groups: [] });
  const [shareAll, setShareAll] = useState(true);
  const [selPeople, setSelPeople] = useState([]);
  const [selGroups, setSelGroups] = useState([]);
  const [ytq, setYtq] = useState("");
  const [ytResults, setYtResults] = useState(null);
  const [ytLoading, setYtLoading] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/media/links"); setPersonal(data.links || []); setShared(data.shared || []); }
    catch {} finally { setLoaded(true); }
  };
  useEffect(() => { if (!loaded) load(); }, [loaded]);
  useEffect(() => {
    if (shareOn && !targets.people.length && !targets.groups.length) {
      api.get("/media/share-targets").then((r) => setTargets(r.data)).catch(() => {});
    }
  }, [shareOn]);

  const list = (scope === "mine" ? personal : shared).filter((l) => l.type === tab);

  const buildShare = () => (shareOn ? {
    shared: true, shared_all: shareAll,
    person_ids: shareAll ? [] : selPeople, group_ids: shareAll ? [] : selGroups,
  } : {});

  const addUrl = async (rawUrl) => {
    const u = (rawUrl ?? url).trim();
    if (!u) return;
    if (!loaded) await load();
    setAdding(true);
    try {
      const { data } = await api.post("/media/links", { url: u, ...buildShare() });
      if (data.scope === "shared") { setShared((p) => (p.some((l) => l.id === data.id) ? p : [data, ...p])); setScope("school"); }
      else { setPersonal((p) => (p.some((l) => l.id === data.id) ? p : [data, ...p])); setScope("mine"); }
      setTab(data.type); setNow(data); setUrl(""); setYtResults(null); setYtq("");
      toast.success(data.scope === "shared" ? "Compartido con el colegio" : "Agregado a tu biblioteca");
    } catch (e) { toast.error(e?.response?.data?.detail || "Enlace no válido"); }
    finally { setAdding(false); }
  };

  const searchYt = async () => {
    if (!ytq.trim()) return;
    setYtLoading(true); setYtResults(null);
    try { const { data } = await api.get("/media/youtube/search", { params: { q: ytq.trim() } }); setYtResults(data.results || []); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo buscar"); }
    finally { setYtLoading(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/media/links/${id}`); setPersonal((p) => p.filter((l) => l.id !== id)); setShared((p) => p.filter((l) => l.id !== id)); if (now?.id === id) setNow(null); }
    catch { toast.error("No se pudo eliminar"); }
  };
  const share = async (l) => {
    try { if (navigator.share) { await navigator.share({ title: l.title, url: l.url }); return; } await navigator.clipboard.writeText(l.url); toast.success("Enlace copiado"); } catch {}
  };
  const toggle = (arr, set, id) => set(arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);

  const playerHeight = now?.type === "youtube"
    ? (isPage ? 380 : 200)
    : (now && SPOTIFY_TALL.test(now.embed_url) ? (isPage ? 420 : 352) : (isPage ? 240 : 152));

  return (
    <div className="flex flex-col min-h-0 flex-1">
      <div className="flex border-b border-nardo-100">
        <button onClick={() => setTab("spotify")} data-testid="media-tab-spotify" className={`flex-1 py-2.5 text-sm font-semibold inline-flex items-center justify-center gap-1.5 ${tab === "spotify" ? "text-emerald-600 border-b-2 border-emerald-500" : "text-nardo-500"}`}><Music className="w-4 h-4" /> Spotify</button>
        <button onClick={() => setTab("youtube")} data-testid="media-tab-youtube" className={`flex-1 py-2.5 text-sm font-semibold inline-flex items-center justify-center gap-1.5 ${tab === "youtube" ? "text-red-600 border-b-2 border-red-500" : "text-nardo-500"}`}><Youtube className="w-4 h-4" /> YouTube</button>
      </div>

      <div className="flex gap-1 p-2 bg-nardo-50">
        <button onClick={() => setScope("mine")} data-testid="media-scope-mine" className={`flex-1 text-xs font-semibold py-1.5 rounded-md ${scope === "mine" ? "bg-white text-navy shadow" : "text-nardo-500"}`}>Mi biblioteca</button>
        <button onClick={() => setScope("school")} data-testid="media-scope-school" className={`flex-1 text-xs font-semibold py-1.5 rounded-md inline-flex items-center justify-center gap-1 ${scope === "school" ? "bg-white text-navy shadow" : "text-nardo-500"}`}><Building2 className="w-3.5 h-3.5" /> Del colegio</button>
      </div>

      {now && (
        <div className="bg-black/5 p-2" data-testid="media-player">
          <iframe key={now.id} title={now.title} src={now.embed_url + (now.type === "spotify" ? "?utm_source=uaicard" : "?rel=0")}
            width="100%" height={playerHeight} style={{ border: 0, borderRadius: 12 }}
            allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture; accelerometer; gyroscope; web-share" allowFullScreen loading="lazy" />
        </div>
      )}

      <div className="p-3 border-b border-nardo-100 space-y-2">
        {tab === "youtube" && (
          <div className="flex gap-2">
            <input value={ytq} onChange={(e) => setYtq(e.target.value)} onKeyDown={(e) => e.key === "Enter" && searchYt()} data-testid="media-yt-search-input"
              placeholder="Buscar en YouTube…" className="flex-1 min-w-0 border border-nardo-200 rounded-lg px-3 py-2 text-sm focus:border-navy outline-none" />
            <button onClick={searchYt} disabled={ytLoading} data-testid="media-yt-search-btn" className="shrink-0 inline-flex items-center bg-red-600 hover:bg-red-700 text-white px-3 rounded-lg disabled:opacity-50">
              {ytLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            </button>
          </div>
        )}
        {ytResults && (
          <div className="max-h-52 overflow-y-auto space-y-1 border border-nardo-100 rounded-lg p-1" data-testid="media-yt-results">
            {ytResults.length === 0 ? <div className="text-xs text-nardo-400 text-center py-3">Sin resultados</div> : ytResults.map((v) => (
              <button key={v.video_id} onClick={() => addUrl(v.url)} data-testid={`media-yt-result-${v.video_id}`} className="w-full flex items-center gap-2 p-1.5 rounded-md hover:bg-nardo-50 text-left">
                <img src={v.thumb} alt="" className="w-16 h-10 object-cover rounded shrink-0" />
                <span className="text-xs text-navy line-clamp-2 flex-1">{v.title}<span className="block text-nardo-400">{v.channel}</span></span>
                <Plus className="w-4 h-4 text-navy shrink-0" />
              </button>
            ))}
          </div>
        )}
        <div className="flex gap-2">
          <input ref={inputRef} value={url} onChange={(e) => setUrl(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addUrl()} data-testid="media-url-input"
            placeholder={tab === "spotify" ? "Pega un enlace de Spotify…" : "…o pega un enlace de YouTube"} className="flex-1 min-w-0 border border-nardo-200 rounded-lg px-3 py-2 text-sm focus:border-navy outline-none" />
          <button onClick={() => addUrl()} disabled={adding} data-testid="media-add-btn" className="shrink-0 inline-flex items-center bg-navy hover:bg-navy-light text-white px-3 rounded-lg disabled:opacity-50">
            {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          </button>
        </div>

        <label className="flex items-center gap-2 text-xs text-navy font-medium cursor-pointer">
          <input type="checkbox" checked={shareOn} onChange={(e) => setShareOn(e.target.checked)} data-testid="media-share-toggle" className="accent-navy" />
          <Share2 className="w-3.5 h-3.5" /> Compartir con el colegio al agregar
        </label>
        {shareOn && (
          <div className="rounded-lg bg-nardo-50 p-2 space-y-2" data-testid="media-share-config">
            <div className="flex gap-1">
              <button onClick={() => setShareAll(true)} data-testid="media-share-all" className={`flex-1 text-[11px] font-semibold py-1 rounded ${shareAll ? "bg-navy text-white" : "bg-white text-nardo-500 border border-nardo-200"}`}>Todo el personal</button>
              <button onClick={() => setShareAll(false)} data-testid="media-share-choose" className={`flex-1 text-[11px] font-semibold py-1 rounded ${!shareAll ? "bg-navy text-white" : "bg-white text-nardo-500 border border-nardo-200"}`}>Elegir</button>
            </div>
            {!shareAll && (
              <div className="max-h-32 overflow-y-auto space-y-0.5">
                {targets.groups.length > 0 && <div className="text-[10px] uppercase tracking-wider text-nardo-400 px-1 pt-1 flex items-center gap-1"><Users className="w-3 h-3" /> Grupos</div>}
                {targets.groups.map((g) => (
                  <label key={g.id} className="flex items-center gap-2 text-xs px-1 py-0.5 cursor-pointer">
                    <input type="checkbox" checked={selGroups.includes(g.id)} onChange={() => toggle(selGroups, setSelGroups, g.id)} data-testid={`media-share-group-${g.id}`} className="accent-navy" />
                    {g.title || "Grupo"}
                  </label>
                ))}
                <div className="text-[10px] uppercase tracking-wider text-nardo-400 px-1 pt-1">Docentes / personal</div>
                {targets.people.map((p) => (
                  <label key={p.id} className="flex items-center gap-2 text-xs px-1 py-0.5 cursor-pointer">
                    <input type="checkbox" checked={selPeople.includes(p.id)} onChange={() => toggle(selPeople, setSelPeople, p.id)} data-testid={`media-share-person-${p.id}`} className="accent-navy" />
                    {p.name}
                  </label>
                ))}
              </div>
            )}
          </div>
        )}
        <p className="text-[11px] text-nardo-400">{tab === "spotify" ? "Para escuchar completa, inicia sesión en Spotify en tu dispositivo." : "Busca o pega un enlace de YouTube."}</p>
      </div>

      <div className="overflow-y-auto flex-1 divide-y divide-nardo-50" data-testid="media-list">
        {list.length === 0 ? (
          <div className="py-8 text-center text-sm text-nardo-400">{scope === "school" ? "El colegio aún no comparte" : "Sin"} {tab === "spotify" ? "música" : "videos"}.</div>
        ) : list.map((l) => (
          <div key={l.id} className={`flex items-center gap-2 px-3 py-2.5 ${now?.id === l.id ? "bg-emerald-50/60" : ""}`} data-testid={`media-link-${l.id}`}>
            <button onClick={() => setNow(l)} data-testid={`media-play-${l.id}`} className="w-8 h-8 shrink-0 rounded-full bg-navy/10 text-navy flex items-center justify-center hover:bg-navy hover:text-white transition-colors"><Play className="w-3.5 h-3.5" /></button>
            <button onClick={() => setNow(l)} className="min-w-0 flex-1 text-left">
              <div className="text-sm font-medium text-navy truncate">{l.title}</div>
              <div className="text-[11px] text-nardo-400 truncate">{l.scope === "shared" && l.shared_by ? `Compartió: ${l.shared_by}` : l.type}</div>
            </button>
            <button onClick={() => share(l)} data-testid={`media-share-${l.id}`} title="Compartir" className="p-1.5 text-nardo-400 hover:text-navy"><Share2 className="w-4 h-4" /></button>
            {(scope === "mine" || l.owned) && (
              <button onClick={() => remove(l.id)} data-testid={`media-delete-${l.id}`} title="Eliminar" className="p-1.5 text-nardo-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function MediaWidget() {
  const [open, setOpen] = useState(false);

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} data-testid="media-fab" title="Música y videos"
        className="fixed bottom-[150px] md:bottom-[86px] right-4 z-[58] w-14 h-14 flex items-center justify-center rounded-full bg-gradient-to-br from-emerald-500 to-navy text-white shadow-xl shadow-emerald-900/30 hover:scale-105 active:scale-95 transition-transform">
        <Music className="w-6 h-6" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-5 right-5 z-[59] w-[92vw] max-w-sm" data-testid="media-panel">
      <div className="bg-white rounded-2xl border border-nardo-200 shadow-2xl shadow-navy/25 overflow-hidden flex flex-col max-h-[85vh]">
        <div className="flex items-center justify-between px-4 py-3 bg-navy text-white">
          <div className="flex items-center gap-2 font-semibold text-sm"><Music className="w-4 h-4" /> Música y videos</div>
          <button onClick={() => setOpen(false)} data-testid="media-close" className="text-white/80 hover:text-white p-1"><X className="w-4 h-4" /></button>
        </div>
        <MediaLibrary variant="widget" />
      </div>
    </div>
  );
}
